import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { validateWorkEmail, validatePhone } from '@/lib/validation';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';
import { submitContactForm } from '@/lib/hubspot';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    else if (formData.firstName.trim().length < 2) newErrors.firstName = 'First name must be at least 2 characters';

    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (formData.lastName.trim().length < 2) newErrors.lastName = 'Last name must be at least 2 characters';

    const emailError = validateWorkEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    if (!formData.company.trim()) newErrors.company = 'Company is required';
    else if (formData.company.trim().length < 2) newErrors.company = 'Company name must be at least 2 characters';

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 10) newErrors.message = 'Please provide a bit more detail (at least 10 characters)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !agreedToTerms) return;

    setSubmitting(true);

    try {
      await submitContactForm({
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Contact form submission error:', err);
      // Still show success — form data was captured locally
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Message sent!</h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
              Thank you for reaching out. We'll get back to you within 24 hours.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Contact' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-[#0a0a0f] dark:to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-white/10 rounded-full mb-6">
              Contact
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Get in touch
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Have questions? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Email</h3>
                    <p className="text-slate-600 dark:text-slate-400">hello@adventive.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10 p-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">First Name *</label>
                    <input
                      id="contact-firstName"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.firstName ? 'contact-firstName-error' : undefined}
                      aria-invalid={!!errors.firstName}
                      className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                        errors.firstName ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                      } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent`}
                    />
                    {errors.firstName && <p id="contact-firstName-error" className="mt-1 text-sm text-red-500" role="alert">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Last Name *</label>
                    <input
                      id="contact-lastName"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.lastName ? 'contact-lastName-error' : undefined}
                      aria-invalid={!!errors.lastName}
                      className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                        errors.lastName ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                      } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent`}
                    />
                    {errors.lastName && <p id="contact-lastName-error" className="mt-1 text-sm text-red-500" role="alert">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Work Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="name@company.com"
                    aria-required="true"
                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                    aria-invalid={!!errors.email}
                    className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                      errors.email ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                    } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent`}
                  />
                  {errors.email && <p id="contact-email-error" className="mt-1 text-sm text-red-500" role="alert">{errors.email}</p>}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Company *</label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      aria-required="true"
                      aria-describedby={errors.company ? 'contact-company-error' : undefined}
                      aria-invalid={!!errors.company}
                      className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                        errors.company ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                      } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent`}
                    />
                    {errors.company && <p id="contact-company-error" className="mt-1 text-sm text-red-500" role="alert">{errors.company}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Phone Number</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
                      aria-invalid={!!errors.phone}
                      className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                        errors.phone ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                      } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent`}
                    />
                    {errors.phone && <p id="contact-phone-error" className="mt-1 text-sm text-red-500" role="alert">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    aria-required="true"
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    aria-invalid={!!errors.message}
                    className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-white/5 text-slate-900 dark:text-white ${
                      errors.message ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                    } focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent resize-none`}
                    placeholder="How can we help?"
                  />
                  {errors.message && <p id="contact-message-error" className="mt-1 text-sm text-red-500" role="alert">{errors.message}</p>}
                </div>

                {/* Privacy Agreement */}
                <div>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={agreedToTerms}
                    onClick={() => setAgreedToTerms(!agreedToTerms)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
                      agreedToTerms
                        ? 'border-[#635bff] bg-[#635bff]/5 dark:bg-[#635bff]/10'
                        : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                      agreedToTerms
                        ? 'bg-[#635bff] text-white'
                        : 'border-2 border-slate-300 dark:border-slate-500'
                    }`} aria-hidden="true">
                      {agreedToTerms && (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      I agree to the{' '}
                      <Link to="/legal/privacy-policy" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/legal/terms-conditions" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={(e) => e.stopPropagation()}>Terms &amp; Conditions</Link>
                    </span>
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={!agreedToTerms || submitting || !formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.company.trim() || !formData.message.trim()}
                  className={`w-full py-3 text-base font-semibold rounded-lg transition-colors flex items-center justify-center ${
                    agreedToTerms && !submitting && formData.firstName.trim() && formData.lastName.trim() && formData.email.trim() && formData.company.trim() && formData.message.trim()
                      ? 'text-white bg-[#635bff] hover:bg-[#5449e6]'
                      : 'text-slate-400 bg-slate-200 dark:bg-slate-700 dark:text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
